import {
  Accordion,
  ActionIcon,
  Box,
  Group,
  Text,
  Tooltip,
} from '@mantine/core';
import { PiCodepenLogo } from 'react-icons/pi';
import { OutputValueDisplay } from '../output-value';
import { EvaluationValue } from '../../types';
import { OptionViewModel } from '../../utils';
import { OptionType } from '@morph-mapper/types';
import { t } from 'i18next';

export type DerivationAccordionItem = {
  key: string;
  title: string;
  graphOptionId?: string;
  output: EvaluationValue;
};

export type DerivationAccordionProps = {
  items: Array<Extract<OptionViewModel, { type: OptionType.Graph }>>;
  onOpen: (key: string, graphOptionId?: string) => void;
};

export const DerivationAccordion = ({
  items,
  onOpen,
}: DerivationAccordionProps) => {
  if (items.length === 0) {
    return (
      <Text fz="xs" c="dimmed">
        {t('graphEditor.derivations.none')}
      </Text>
    );
  }

  return (
    <Accordion
      radius="xs"
      multiple
      defaultValue={items.map((item) => item.key)}
      sx={{
        width: '100%',
        '.derivation-open': {
          opacity: 0,
          transition: 'opacity 150ms ease',
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: 1,
        },
        '.derivation-item:hover .derivation-open': {
          opacity: 1,
        },
      }}
      styles={(theme) => ({
        border: 0,
        item: {
          border: 0,
          borderBottom: `1px solid ${theme.colors.gray[2]}`,
        },
        control: {
          backgroundColor: theme.white,
        },
        panel: {
          backgroundColor: theme.colors.gray[0],
          borderLeft: 0,
          borderRight: 0,
          borderTop: `1px solid ${theme.colors.gray[2]}`,
          paddingTop: theme.spacing.sm,
        },
      })}
    >
      {items.map((item) => (
        <Accordion.Item
          key={item.key}
          value={item.key}
          className="derivation-item"
        >
          <Accordion.Control>
            <Group position="apart" noWrap>
              <Text fz="xs" fw={600}>
                {t(item.config.title)}
              </Text>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Box sx={{ position: 'relative' }}>
              <Tooltip label={t('graphEditor.derivations.open')} withArrow>
                <ActionIcon
                  className="derivation-open"
                  size="xs"
                  variant="subtle"
                  color="orange"
                  aria-label={t('graphEditor.derivations.open')}
                  onClick={() => onOpen(item.key, item.graphId)}
                >
                  <PiCodepenLogo />
                </ActionIcon>
              </Tooltip>
              <OutputValueDisplay
                value={item.value}
                maxLength={Number.MAX_SAFE_INTEGER}
                emptyLabel={t('graphEditor.common.empty')}
              />
            </Box>
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );
};
